import * as React from "react";
import { IShareWidgetProps, IShareWidgetState } from "./ShareWidget.types";
import { Icon } from "../Icon/icon";
import { KeyboardEventKeys } from "../../utils/keyboardEventKeys";
import { pushTagManagerEvent } from "../../utils/helpers";

/**
 * Renders a share widget to be able share the current page on different platforms as well as with e-mail
 */
export class ShareWidget extends React.Component<
  IShareWidgetProps,
  IShareWidgetState
> {
  private _shareContainer: HTMLElement = null;
  constructor(props: IShareWidgetProps) {
    super(props);
    this.toggleShareWidgetOpenState =
      this.toggleShareWidgetOpenState.bind(this);
    this.copyLink = this.copyLink.bind(this);
    this.state = {
      isOpen: false,
      encodedPageUrl: encodeURIComponent(this.props.pageUrl),
    };
  }

  /**
   * A small hack to be able to make copy command work. A temporary hidden input is created where the content is the page link. The content is then copied.
   */
  copyLink = (e: React.MouseEvent): void => {
    e.preventDefault();
    var input = document.createElement("input");
    input.id = "copylink";
    input.type = "text";
    input.style.position = "absolute";
    input.style.left = "-9999px";
    input.style.top = "0";
    input.value = this.props.pageUrl;
    document.body.appendChild(input);
    input.select();
    try {
      document.execCommand("copy");
    } catch (err) {}
    input.parentNode.removeChild(input);
    pushTagManagerEvent("share", "Dela", "Kopiera länk");
  };

  public toggleShareWidgetOpenState(e): void {
    e.preventDefault();
    this.setState((prevState) => ({
      isOpen: !prevState.isOpen,
    }));
  }

  isInPage(node: Node) {
    return node === document.body ? false : this._shareContainer.contains(node);
  }

  /**
   * We want to be able to close the widget by clicking outside its boundries and by ESC key. Therefore the eventlistener is added to the document.
   */
  componentDidMount() {
    document.addEventListener("click", (e) => {
      if (this.state.isOpen && !this.isInPage(e.target as Node)) {
        this.toggleShareWidgetOpenState(e);
      }
    });

    document.addEventListener("keyup", (e) => {
      if (this.state.isOpen && e.key === KeyboardEventKeys.esc) {
        this.toggleShareWidgetOpenState(e);
      }
    });
  }

  /**
   * This is a helper method to be able track which link is clicked.
   * It should be enough the just check the minimal amount of chars with startsWith function.
   */
  onShareItemClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const link = e.currentTarget.href;
    let action = "";

    if (link.startsWith("https://www.fa")) {
      action = "Facebook";
    } else if (link.startsWith("https://tw")) {
      action = "Twitter";
    } else if (link.startsWith("https://www.li")) {
      action = "LinkedIn";
    } else if (link.startsWith("mail")) {
      action = "E-mail";
    }

    if (action !== "") {
      pushTagManagerEvent("share", "Dela", action);
    }
  };

  render(): JSX.Element {
    return (
      <div
        ref={(el) => (this._shareContainer = el)}
        id="share-widget-container"
        className="share-widget"
      >
        <button
          onClick={this.toggleShareWidgetOpenState}
          className="stripped-button stripped-button--sharewidget"
          aria-expanded={this.state.isOpen}
          aria-controls="share-widget"
        >
          <Icon iconType="fontawesome/Share" className="share-widget__btn" />
          {this.props.shareText}
        </button>
        <div
          className={`share-widget__container ${
            this.state.isOpen
              ? "share-widget__container--open drop-shadow-low-elevation"
              : ""
          }`}
          id="share-widget"
        >
          <div>
            <a
              target="_blank"
              rel="noreferrer"
              href={`https://www.facebook.com/sharer/sharer.php?u=${this.state.encodedPageUrl}`}
              onClick={this.onShareItemClick}
            >
              <Icon
                iconType="socialmedia/facebook-square"
                className="share-widget__icon"
              />
              Facebook
            </a>
          </div>
          <div>
            <a
              target="_blank"
              rel="noreferrer"
              href={`https://twitter.com/home?status=${this.state.encodedPageUrl}`}
              id="share-twitter"
              onClick={this.onShareItemClick}
            >
              <Icon
                iconType="socialmedia/x-twitter"
                className="share-widget__icon"
              />
              Twitter
            </a>
          </div>
          <div>
            <a
              target="_blank"
              rel="noreferrer"
              href={encodeURI(
                `https://www.linkedin.com/sharing/share-offsite/?url=${this.props.pageUrl}`
              )}
              onClick={this.onShareItemClick}
            >
              <Icon
                iconType="socialmedia/LinkedIn"
                className="share-widget__icon"
              />
              LinkedIn
            </a>
          </div>
          <div>
            <a
              href={`mailto:?body=${this.state.encodedPageUrl}`}
              onClick={this.onShareItemClick}
            >
              <Icon
                iconType="socialmedia/envelope-square"
                className="share-widget__icon"
              />
              E-mail
            </a>
          </div>
          <div>
            <a href="/" onClick={this.copyLink}>
              <Icon
                iconType="socialmedia/link"
                className="share-widget__icon"
              />
              {this.props.copyLinkText}
            </a>
          </div>
        </div>
      </div>
    );
  }
}
